import * as React from 'react';
import { defaultConfig } from '@eg-maps/core';
import { EGDSLayoutPosition } from '@egds/react-core/layout-position';
import { EGDSSkeleton } from '@egds/react-core/skeleton';
import { initMapBoxApi, initGoogleMapsApi } from '@egds/react-core/utils';
import { MapType } from '../../types/index.esm.js';
const useIsMounted = () => {
  const isMounted = React.useRef(false);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return isMounted;
};
const MapScriptLoader = ({
  width,
  height,
  mapType,
  mapTypeVersion,
  accessToken,
  children,
  locale,
  googleMapsId,
  reloadMapProviderSDK = false,
  onError
}) => {
  const isMounted = useIsMounted();
  const [loadingCount, setLoadingCount] = React.useState(1);
  const [error, setError] = React.useState();
  const egMapsConfig = defaultConfig();
  React.useEffect(() => {
    const timeoutDuration = 10000;
    const timeoutPromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        reject(new Error('Timeout: Map API initialization took too long'));
      }, timeoutDuration);
    });
    switch (mapType) {
      case MapType.GOOGLE:
        {
          const baseUrl = egMapsConfig.googleMaps?.urls?.api;
          if (baseUrl) {
            const conf = {
              accessToken,
              mapId: googleMapsId,
              locale,
              version: mapTypeVersion
            };
            Promise.race([initGoogleMapsApi(reloadMapProviderSDK, conf, false), timeoutPromise]).then(() => {
              if (isMounted.current) {
                setLoadingCount(0);
              }
            }).catch(err => {
              setError(err.message);
              if (typeof onError === 'function') onError(err);
            });
          } else {
            const err = {
              message: 'No base URL found in eg-maps config to load google maps sdk'
            };
            setError(err.message);
            if (typeof onError === 'function') onError(err);
          }
        }
        break;
      case MapType.MAPBOX:
        {
          const conf = {
            version: mapTypeVersion
          };
          Promise.race([initMapBoxApi(reloadMapProviderSDK, conf), timeoutPromise]).then(() => {
            if (isMounted.current) {
              setLoadingCount(0);
            }
          }).catch(err => {
            setError(err.message);
            if (typeof onError === 'function') onError(err);
          });
        }
        break;
      default:
        {
          const err = {
            message: `Map provider not supported: ${mapType}`
          };
          setError(err.message);
          if (typeof onError === 'function') onError(err);
        }
    }
  }, []);
  if (loadingCount > 0 && !error) {
    return React.createElement("div", {
      style: {
        width,
        height
      }
    }, React.createElement(EGDSLayoutPosition, {
      type: "relative",
      position: {
        top: 'center',
        left: 'center'
      }
    }, React.createElement("div", {
      style: {
        width,
        height,
        position: 'relative'
      }
    }, React.createElement(EGDSSkeleton, {
      type: "block"
    }))));
  }
  return React.createElement(React.Fragment, null, children);
};
/*
  This is a HOC that loads the google/mapbox JS api before rendering the child component that is passed as a parameter.
*/
const withScriptLoader = (childComponent, props) => {
  const scriptLoaderProps = {
    accessToken: props.accessToken,
    googleMapsId: props.mapConfig?.googleMapsId,
    locale: props.mapConfig?.locale,
    mapType: props.mapType,
    mapTypeVersion: props.mapTypeVersion,
    width: props.width,
    height: props.height,
    reloadMapProviderSDK: props.reloadMapProviderSDK,
    onError: props.onError
  };
  return React.createElement(MapScriptLoader, {
    ...scriptLoaderProps
  }, childComponent);
};
export { MapScriptLoader, withScriptLoader };